'use strict'

import * as common from 'assets/core/js/common'

export interface AutocompleteResultValue {
  type: string
  id: number
  name: string
  nameHighlight: string
  description: string
}

export interface AutocompleteResult {
  data: AutocompleteResultValue | AutocompleteResultValue[]
}

export interface AutocompleteConfig {
  callbackClose?(): void
  callbackQuery(value: string): void
  callbackDisplayResults?(value: string): void
  callbackEmptyInput?(): void
  callbackOpen?(): void
  callbackPreRender?(resultEl: HTMLDivElement, value: AutocompleteResultValue): HTMLDivElement
  callbackSelect?(value: AutocompleteResultValue): void
  callbackClick?(): void
  minLength?: number
  queryParameter?: string
  delay?: number
  dataSource?: string
  dataElement?: null | string | HTMLSelectElement
  closeIfMinLengthBelow?: boolean
  formatLabel?(result: { name: string }): string
}

export const Events = {
  AUTOCOMPLETE_OPENED: 'autocomplete.opened',
  AUTOCOMPLETE_CLOSED: 'autocomplete.closed',
  AUTOCOMPLETE_ITEM_SELECTED: 'autocomplete.item.selected',
}

const defaultConfig: AutocompleteConfig = {
  callbackClose: function () {
    /* nothing */
  },
  callbackQuery: function () {
    /* nothing */
  },
  callbackDisplayResults: function () {
    /* nothing */
  },
  callbackEmptyInput: function () {
    /* nothing */
  },
  callbackOpen: function () {
    /* nothing */
  },
  callbackPreRender: function (resultEl) {
    /* istanbul ignore next */
    return resultEl
  },
  callbackSelect: function () {
    /* nothing */
  },
  callbackClick: function () {
    /* nothing */
  },
  minLength: 2,
  queryParameter: 'query',
  delay: 300,
  dataSource: 'xhr',
  dataElement: null,
  closeIfMinLengthBelow: true,
  formatLabel: function (result) {
    /* istanbul ignore next */
    return result?.name
  },
}

export default class Autocomplete {
  config!: Partial<AutocompleteConfig>
  element!: Element
  selectEl!: HTMLSelectElement
  events!: {
    inputClick?(e?: MouseEvent | TouchEvent | Event): void
    inputKeyUp?(e?: KeyboardEvent | TouchEvent | Event): void
    inputInput?(): void
  }

  cache!: Record<string, AutocompleteResult>
  inputEl!: HTMLInputElement
  resultsEl!: Element
  wrapperEl!: HTMLElement
  mobilebottomPanelWrapperEl!: HTMLElement
  selectedRow!: number
  searchValue!: string
  previousRequest!: null | XMLHttpRequest
  resultsVisible!: boolean
  onKeyUpTimeout!: ReturnType<typeof setTimeout>

  constructor(element: string | HTMLElement, userConfig: Partial<AutocompleteConfig>) {
    let el: Element | string | null = element

    if (typeof element === 'string') {
      el = document.querySelector(element)
    }

    if (!el || typeof el === 'string') {
      return
    }

    this.element = el
    this.config = Object.assign({}, defaultConfig, userConfig)
    this.config.callbackClose = this.config.callbackClose?.bind(this)
    this.config.callbackEmptyInput = this.config.callbackEmptyInput?.bind(this)
    this.config.callbackOpen = this.config.callbackOpen?.bind(this)
    this.config.callbackPreRender = this.config.callbackPreRender?.bind(this)
    this.config.callbackSelect = this.config.callbackSelect?.bind(this)
    this.config.callbackClick = this.config.callbackClick?.bind(this)

    this.events = {}
    this.cache = {}

    this.inputEl = this.element.querySelector('.autocomplete-field') as HTMLInputElement
    this.resultsEl = this.element.querySelector('.autocomplete-results') as HTMLElement
    this.wrapperEl = this.element.querySelector('.autocomplete-results-wrapper') as HTMLElement
    this.mobilebottomPanelWrapperEl = this.element.querySelector('.o-mobile-bottom-panel__wrapper') as HTMLElement
    this.selectedRow = -1
    this.searchValue = this.sanitize(this.inputEl.value)
    this.previousRequest = null
    this.resultsVisible = false

    if (this.config.dataSource === 'select' && this.config.dataElement) {
      this.initSelectEl()
    }

    document.body.addEventListener('click', (e) => {
      if (
        !this.element.contains(e.target as HTMLElement) &&
        !this.mobilebottomPanelWrapperEl.contains(e.target as HTMLElement) &&
        this.resultsVisible
      ) {
        this.close()
      }
    })

    this.destroy = this.destroy.bind(this)
    this.initEvents()
  }

  initEvents(): void {
    this.events.inputClick = () => {
      this.config.callbackClick && this.config.callbackClick()

      if (this.cache[this.searchValue] && !this.resultsVisible && this.inputEl.value !== '') {
        // @ts-expect-error this is valid
        this.displayResults(this.cache[this.searchValue])
      } else if (this.inputEl.value !== '') {
        this.query()
      }
    }

    this.events.inputInput = () => {
      // @ts-expect-error object is not undefined
      if (this.config.minLength <= this.inputEl.value.length && this.searchValue !== this.inputEl.value) {
        this.resultsEl.innerHTML = ''
        this.searchValue = this.sanitize(this.inputEl.value)
        this.selectedRow = -1

        if (this.cache[this.searchValue]) {
          // @ts-expect-error object is not undefined
          this.displayResults(this.cache[this.searchValue])
        } else {
          clearTimeout(this.onKeyUpTimeout)

          this.onKeyUpTimeout = setTimeout(() => {
            if (this.previousRequest && this.previousRequest.readyState < XMLHttpRequest.DONE) {
              this.previousRequest.abort()
            }

            // @ts-expect-error object is not undefined
            if (this.config.minLength <= this.inputEl.value.length && this.searchValue !== '') {
              this.query()
            }
          }, this.config.delay)
        }
        // @ts-expect-error object is not undefined
      } else if (this.config.minLength > this.inputEl.value.length) {
        this.searchValue = this.sanitize(this.inputEl.value)

        if (this.resultsVisible && this.config.closeIfMinLengthBelow) {
          this.close()
        }

        if (this.searchValue.length === 0) {
          if (this.previousRequest && this.previousRequest.readyState < XMLHttpRequest.DONE) {
            this.previousRequest.abort()
          }

          this.resultsEl.innerHTML = ''
          this.config.callbackEmptyInput && this.config.callbackEmptyInput()
        }
      }
    }

    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.inputEl.addEventListener('pointerup', this.events.inputClick)
    // @ts-expect-error we want the overload
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.inputEl.addEventListener('keyup', this.events.inputKeyUp)
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.inputEl.addEventListener('input', this.events.inputInput)
    this.inputEl.addEventListener('clear', () => {
      if (this.inputEl.value === '') {
        // @ts-expect-error object is not undefined
        this.config.callbackEmptyInput()
        this.resultsEl.innerHTML = ''
        this.resultsEl.setAttribute('hidden', 'hidden')
      }
    })
  }

  sanitize(content: string): string {
    if (content) {
      const node = document.createElement('div')
      node.innerHTML = content

      return node.textContent || ''
    }

    return ''
  }

  destroy(): void {
    // @ts-expect-error we want the overload
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.inputEl.removeEventListener('touchstart', this.events.inputClick)
    // @ts-expect-error we want the overload
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.inputEl.removeEventListener('click', this.events.inputClick)
    // @ts-expect-error we want the overload
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.inputEl.removeEventListener('keyup', this.events.inputKeyUp)
  }

  initSelectEl(): void {
    if (typeof this.config.dataElement === 'string') {
      this.selectEl = document.querySelector(this.config.dataElement) as HTMLSelectElement
    } else {
      this.selectEl = this.config.dataElement as HTMLSelectElement
    }

    if (!this.selectEl) {
      throw new Error('Data source element not found.')
    }

    if (this.selectEl.nodeName !== 'SELECT') {
      throw new Error('Data source is not a select.')
    }

    if (this.selectEl?.options[this.selectEl.selectedIndex]?.value) {
      this.inputEl.value = this.selectEl?.options[this.selectEl.selectedIndex]?.text as string
    }
  }

  query(): void {
    this.config.callbackQuery && this.config.callbackQuery(this.searchValue)
    let url = this.inputEl.getAttribute('data-url')
    const loadingMessage = this.inputEl.getAttribute('data-loading-message')

    this.resultsEl.removeAttribute('hidden')
    this.resultsVisible = true

    if (loadingMessage && !this.resultsEl.querySelector('.autocomplete-result-message')) {
      const loadingEl = document.createElement('div')
      loadingEl.className = 'autocomplete-result-message'
      loadingEl.innerHTML = loadingMessage
      this.resultsEl.innerHTML = ''
      this.resultsEl.appendChild(loadingEl)

      this.open()
    }

    if (this.config.dataSource === 'xhr' && url) {
      const queryStringParams = []
      const params = this.getQueryParameters()
      for (const name in params) {
        if (!Object.prototype.hasOwnProperty.call(params, name)) {
          continue
        }
        queryStringParams.push(`${name}=${encodeURIComponent(params[name] as string)}`)
      }
      url = url + (url.indexOf('?') === -1 ? '?' : '&') + queryStringParams.join('&')

      const xhr = common.createXhr('GET', url)

      xhr.onreadystatechange = () => {
        if (xhr.readyState === XMLHttpRequest.DONE && xhr.status === 200) {
          try {
            if (typeof xhr.response === 'string') {
              const data = JSON.parse<AutocompleteResult>(xhr.response)

              this.displayResults(data)
              this.cache[this.searchValue] = data
            }
          } catch {
            this.displayNoResults()
          }
        } else {
          this.displayNoResults()
        }
      }

      xhr.send(null)

      this.previousRequest = xhr
    } else if (this.config.dataSource === 'select') {
      let data: AutocompleteResultValue[] = []

      try {
        // @ts-expect-error no real error
        data = this.parseSelectDataSource().filter((obj) => {
          return obj.name.match(new RegExp(this.searchValue, 'i'))
        })
      } catch {
        data = []
      }

      this.displayResults({
        data,
      })
    }
  }

  getQueryParameters(): Record<string, string> {
    const params: Record<string, string> = {}
    params[this.config.queryParameter as string] = this.searchValue

    return params
  }

  parseSelectDataSource(): { value: string; name: string }[] {
    const data: { value: string; name: string }[] = []
    const options = this.selectEl.querySelectorAll('option')

    options.forEach((option) => {
      data.push({
        value: option.value,
        name: option.text,
      })
    })

    return data
  }

  displayResults(data: AutocompleteResult): void {
    this.config.callbackDisplayResults && this.config.callbackDisplayResults(this.searchValue)
    this.resultsEl.innerHTML = ''

    if (data.data && Array.isArray(data.data) && data.data.length > 0) {
      data.data.forEach((value) => {
        if (value.name) {
          let resultEl = document.createElement('div')
          resultEl.className = 'autocomplete-result'
          resultEl.innerHTML = value.nameHighlight

          resultEl.addEventListener('click', () => {
            if (this.config.formatLabel) {
              this.inputEl.value = this.config.formatLabel(value)
            }
            this.selectedRow = -1

            if (this.config.callbackSelect) {
              this.config.callbackSelect(value)
            }

            this.close()
          })

          resultEl.addEventListener('mouseover', (e) => {
            const focusedEl = this.resultsEl.getElementsByClassName('autocomplete-result-focus')[0]
            if (focusedEl && e.target !== focusedEl) {
              focusedEl.classList.remove('autocomplete-result-focus')
            }

            resultEl.classList.add('autocomplete-result-focus')

            this.selectedRow = -1
          })

          if (this.config.callbackPreRender) {
            resultEl = this.config.callbackPreRender(resultEl, value) ?? resultEl
          }

          this.resultsEl.appendChild(resultEl)
        }
      })

      if (this.inputEl.value !== '') {
        this.resultsEl.removeAttribute('hidden')
        this.open()
      }
    } else {
      this.displayNoResults()
    }
  }

  displayNoResults(): void {
    const noResultsMessage = this.inputEl.getAttribute('data-no-results-message') as string
    const noResultEl = document.createElement('div')
    noResultEl.className = 'autocomplete-result-message'
    noResultEl.innerHTML = noResultsMessage
    this.resultsEl.innerHTML = ''
    this.resultsEl.appendChild(noResultEl)

    this.open()
  }

  open(): void {
    this.wrapperEl.removeAttribute('hidden')
    this.resultsVisible = true

    if (this.config.callbackOpen) {
      this.config.callbackOpen()
    }

    this.element.dispatchEvent(new CustomEvent(Events.AUTOCOMPLETE_OPENED))
  }

  close(): void {
    this.wrapperEl.setAttribute('hidden', 'hidden')
    this.resultsVisible = false

    if (this.config.callbackClose) {
      this.config.callbackClose()
    }

    this.element.dispatchEvent(new CustomEvent(Events.AUTOCOMPLETE_CLOSED))
  }
}
